import { z } from 'zod';
import { estimateSummarySchema } from '../../estimates/schemas/estimates.schemas.js';
import { signedMoneySchema } from '../../common/schemas/money.schemas.js';
/**
 * A money figure the dashboard adds up, with the number of records behind it that are **still an
 * estimate** (plan §5.7; PP §3). A total that hides how much of itself is provisional is a total a
 * manager would quote in a meeting and then have to correct.
 */
export const dashboardMoneySchema = z.object({
    amount: signedMoneySchema,
    provisionalCount: z.number().int().min(0),
});
/**
 * Days in stock, bucketed (PP §3: "0–30, 31–60, 61–90 and >90 days"). Only vehicles that are still in
 * stock are counted: a sold car has stopped ageing and belongs to the sale figures instead.
 */
export const dashboardAgingSchema = z.object({
    d0_30: z.number().int().min(0),
    d31_60: z.number().int().min(0),
    d61_90: z.number().int().min(0),
    over90: z.number().int().min(0),
});
/** The dashboard counters of plan §5.7, every one of them a SQL aggregate over `numeric`. */
export const dashboardStatsOutputSchema = z.object({
    /** Estimates that are neither `DEAL_AGREED` nor `NO_DEAL`. */
    openEstimates: z.number().int().min(0),
    /** Estimates in `OFFER_SHARED`. */
    offersAwaitingResponse: z.number().int().min(0),
    /** Active stock: every vehicle that is not `SOLD` (plan §5.7). */
    vehiclesInStock: z.number().int().min(0),
    /** All time. */
    soldVehicles: z.number().int().min(0),
    /** Sold within the tenant's current business month, by `sale_date`. */
    soldThisMonth: z.number().int().min(0),
    /** Σ of the latest snapshot's projected total cost over the vehicles still in stock. */
    stockCost: dashboardMoneySchema,
    /** Σ (`sale_price` − latest projected total cost) over sold vehicles — never net company profit. */
    grossContribution: dashboardMoneySchema,
    aging: dashboardAgingSchema,
});
/**
 * One row of "Needs attention" (plan §5.7).
 *
 * `label` is the record's own identity — a stock or estimate number and what the car is — never prose:
 * the queue's title is translated by the page, and the row is data.
 */
export const dashboardQueueRowSchema = z.object({
    id: z.string(),
    label: z.string(),
    /** When the clock started: the request, the offer, the move into service, the sale. */
    since: z.string(),
    /** Where the row goes when it is clicked, relative to the app root. */
    link: z.string(),
});
/** How long a record may sit in each queue before it is worth somebody's attention (plan §5.7). */
export const STALE_OFFER_DAYS = 7;
export const LONG_IN_SERVICE_DAYS = 30;
export const dashboardQueuesOutputSchema = z.object({
    /** ADMIN only — an empty array for a salesperson, who has nothing to decide (plan §5.7). */
    pendingApprovals: z.array(dashboardQueueRowSchema),
    /** `OFFER_SHARED` estimates older than seven days. */
    staleOffers: z.array(dashboardQueueRowSchema),
    /** Vehicles that have been `IN_SERVICE` for more than thirty days. */
    longInService: z.array(dashboardQueueRowSchema),
    /** Sold vehicles whose latest snapshot is still provisional. */
    unresolvedCostsOnSold: z.array(dashboardQueueRowSchema),
});
export const DEFAULT_RECENT_ESTIMATES = 5;
export const recentEstimatesInputSchema = z.object({
    limit: z.coerce.number().int().min(1).max(50).default(DEFAULT_RECENT_ESTIMATES),
});
export const recentEstimatesOutputSchema = z.array(estimateSummarySchema);
//# sourceMappingURL=dashboard.schemas.js.map